// Части экрана тренировки: шапка, таймеры отдыха и подхода, состояние отправки, подход, упражнение.

import { useEffect, useRef, useState } from 'react'

import { Badge, Button, Icon, IconButton, NumberStepper } from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatClock } from '@/lib/format'
import { useRestTimer } from '@/lib/restTimer'
import type { ActiveExercise, ActiveSet, ActiveWorkout } from '@/offline/activeSession'
import { dismissFailed, isAuthBlocked, useFailedMutations, useOnline, usePendingCount } from '@/offline/queue'
import { ExerciseActions } from '@/pages/exercises/ExerciseActions'
import { useExerciseMuscle, type Exercise } from '@/pages/workouts/api'
import { formatSet, setFields } from '@/pages/workouts/logic'

import {
  addSet,
  cancelSetTimer,
  changeSet,
  moveSet,
  pauseWorkout,
  removeSet,
  resumeWorkout,
  startSetTimer,
  toggleSet,
} from './actions'
import { useNow } from './data'
import { scrollUnderHeads, scrollUnderHeadsAfterKeyboard } from './layout'
import { doneCount, exerciseProgress, WEIGHT_STEP, workoutElapsed } from './logic'
import { MusclesButton } from './MusclesSheet'
import { useSetTimer, type SetTimerSnapshot } from './setTimer'

/** «выполнено/всего»; зелёным, когда всё выполнено. */
function Count({ done, total }: { done: number; total: number }) {
  return (
    <span className={cn('font-medium tabular-nums', total > 0 && done === total ? 'text-accent' : 'text-fg')}>
      {done}/{total}
    </span>
  )
}

/**
 * Липкая шапка тренировки: название, сколько идёт, упражнения «2/5» и подходы «7/15».
 * Прижата к верху экрана (под вырезом); под ней липнет заголовок раскрытого упражнения.
 */
export function WorkoutHead({ workout }: { workout: ActiveWorkout }) {
  return workout.draft ? <DraftHead workout={workout} /> : <RunningHead workout={workout} />
}

const HEAD_CLASS =
  'sticky top-0 z-20 -mx-4 -mt-[calc(1rem+env(safe-area-inset-top))] border-b border-border bg-bg/95 px-4 pb-2.5 pt-[calc(0.75rem+env(safe-area-inset-top))] backdrop-blur lg:-mx-8 lg:-mt-7 lg:px-8 lg:pt-4'

/** Шапка неначатой тренировки: вместо секундомера — «не начата», сколько упражнений и подходов. */
function DraftHead({ workout }: { workout: ActiveWorkout }) {
  const totalSets = workout.exercises.reduce((n, e) => n + e.sets.length, 0)
  return (
    <header data-workout-head className={HEAD_CLASS}>
      <div className="flex items-baseline gap-3">
        <h1 className="min-w-0 flex-1 truncate text-xl font-semibold tracking-tight text-fg lg:text-2xl">
          {workout.template_name ?? 'Тренировка'}
        </h1>
        <Badge className="shrink-0">не начата</Badge>
        <MusclesButton workout={workout} className="self-center" />
      </div>
      <p className="mt-0.5 text-sm text-muted">
        Упражнения <span className="font-medium tabular-nums text-fg">{workout.exercises.length}</span> · Подходы{' '}
        <span className="font-medium tabular-nums text-fg">{totalSets}</span>
      </p>
    </header>
  )
}

function RunningHead({ workout }: { workout: ActiveWorkout }) {
  const now = useNow(1000)
  const elapsed = workoutElapsed(workout, now)
  const paused = Boolean(workout.paused_at)
  const progress = exerciseProgress(workout)
  const done = doneCount(workout)
  const totalSets = workout.exercises.reduce((n, e) => n + e.sets.length, 0)
  return (
    <header data-workout-head className={HEAD_CLASS}>
      <div className="flex items-center gap-2">
        <h1 className="min-w-0 flex-1 truncate text-xl font-semibold tracking-tight text-fg lg:text-2xl">
          {workout.template_name ?? 'Тренировка'}
        </h1>
        {paused ? <Badge className="shrink-0">пауза</Badge> : null}
        <span className={cn('shrink-0 text-xl font-semibold tabular-nums', paused ? 'text-muted' : 'text-accent')}>
          {formatClock(elapsed)}
        </span>
        <MusclesButton workout={workout} />
        <IconButton
          icon={paused ? 'play' : 'pause'}
          label={paused ? 'Продолжить тренировку' : 'Пауза'}
          variant={paused ? 'primary' : 'secondary'}
          className="-my-1.5 shrink-0"
          onClick={() => void (paused ? resumeWorkout() : pauseWorkout())}
        />
      </div>
      <p className="mt-0.5 text-sm text-muted">
        Упражнения <Count done={progress.done} total={progress.total} /> · Подходы <Count done={done} total={totalSets} />
      </p>
    </header>
  )
}

/** Полоса таймера отдыха: крупные цифры и кнопки ±15 с, «Хватит». После окончания — перебор в минус на красном. */
export function RestTimerBar({ className }: { className?: string }) {
  const timer = useRestTimer()
  if (!timer.running) return null
  const over = timer.remaining === 0
  return (
    <div
      role="timer"
      aria-label={over ? 'Отдых окончен' : 'Отдых'}
      className={cn(
        // Непрозрачный фон (surface + оттенок акцента), как у плашки тренировки: кнопки под ним не просвечивают.
        'flex items-center gap-2 rounded-xl border px-3 py-2 shadow-lg shadow-black/30',
        over
          ? 'border-danger bg-danger text-danger-fg'
          : 'border-accent/40 bg-surface bg-linear-to-r from-accent-soft to-accent-soft text-accent',
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wide">{over ? 'Отдых окончен' : 'Отдых'}</p>
        <p className="text-3xl font-semibold leading-tight tabular-nums">
          {over ? `−${formatClock(timer.overtime)}` : formatClock(timer.remaining)}
        </p>
      </div>
      {over ? null : (
        <>
          <Button size="sm" variant="ghost" className="min-w-11 text-accent" onClick={() => timer.add(-15)} aria-label="Минус 15 секунд">
            −15
          </Button>
          <Button size="sm" variant="ghost" className="min-w-11 text-accent" onClick={() => timer.add(15)} aria-label="Плюс 15 секунд">
            +15
          </Button>
        </>
      )}
      <Button size="sm" variant="secondary" onClick={timer.stop}>
        Хватит
      </Button>
    </div>
  )
}

/** Что показать на таймере подхода: до цели — сколько осталось, после — перебор с «+». */
function setClock(timer: SetTimerSnapshot): { clock: string; reached: boolean } {
  if (timer.target === null) return { clock: formatClock(timer.elapsed), reached: false }
  if (timer.elapsed < timer.target) return { clock: formatClock(timer.target - timer.elapsed), reached: false }
  return { clock: `+${formatClock(timer.elapsed - timer.target)}`, reached: true }
}

function setTimerLabel(timer: SetTimerSnapshot, reached: boolean): string {
  if (timer.target === null) return 'Секундомер'
  return reached ? 'Цель взята' : `Цель ${formatClock(timer.target)}`
}

/** Полоса таймера подхода на время: упражнение, отсчёт до цели (или секундомер), «Стоп». */
export function SetTimerBar({ className }: { className?: string }) {
  const timer = useSetTimer()
  if (!timer.running || !timer.exerciseKey || !timer.setId) return null
  const { clock, reached } = setClock(timer)
  const exerciseKey = timer.exerciseKey
  const setId = timer.setId
  return (
    <div
      role="timer"
      aria-label={`Подход: ${timer.name}`}
      className={cn(
        'flex items-center gap-2 rounded-xl border px-3 py-2 shadow-lg shadow-black/30',
        reached
          ? 'border-accent bg-accent text-accent-fg'
          : 'border-accent/40 bg-surface bg-linear-to-r from-accent-soft to-accent-soft text-accent',
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium uppercase tracking-wide">
          {timer.name} · {setTimerLabel(timer, reached)}
        </p>
        <p className="text-3xl font-semibold leading-tight tabular-nums">{clock}</p>
      </div>
      <Button size="sm" variant="secondary" icon="stop" onClick={() => void toggleSet(exerciseKey, setId)}>
        Стоп
      </Button>
    </div>
  )
}

/** «Офлайн · 3 не отправлено», «Отправляется…», «Всё отправлено» и отказы сервера. */
export function SyncStatus() {
  const online = useOnline()
  const pending = usePendingCount()
  const failed = useFailedMutations()
  let text: string
  let tone: 'neutral' | 'warning' | 'accent' = 'neutral'
  if (!online) {
    text = `Офлайн · ${pending} не отправлено`
    tone = 'warning'
  } else if (pending > 0 && isAuthBlocked()) {
    text = `Войди заново, чтобы отправить ${pending}`
    tone = 'warning'
  } else if (pending > 0) {
    text = `Отправляется · ${pending}`
    tone = 'accent'
  } else {
    text = 'Всё сохранено на сервере'
  }
  return (
    <div className="flex flex-col gap-2">
      <div role="status" aria-live="polite">
        <Badge tone={tone} className="px-2 py-1 text-sm">
          <Icon name={online ? (pending ? 'refresh' : 'check') : 'wifi-off'} size={14} />
          {text}
        </Badge>
      </div>
      {failed.length > 0 ? (
        <div role="alert" className="rounded-lg border border-danger/40 bg-danger-soft p-3 text-sm text-danger">
          <p className="font-medium">Сервер не принял {failed.length === 1 ? 'изменение' : `изменений: ${failed.length}`}</p>
          <ul className="mt-1 space-y-1">
            {failed.map((f) => (
              <li key={f.id} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 break-words">{f.message}</span>
                <IconButton icon="close" label="Скрыть" onClick={() => void dismissFailed(f.id!)} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

function SetEditor({
  exercise,
  set,
  number,
  previous,
  total,
  draft,
  onCompleted,
  onCollapse,
}: {
  exercise: ActiveExercise
  set: ActiveSet
  number: number
  /** Тренировка не начата: подход только правится, отметки «Выполнен» нет. */
  draft: boolean
  /** Подход перед этим — из него «Копировать» берёт вес, повторы, время, дистанцию. */
  previous: ActiveSet | null
  /** Сколько подходов в упражнении — для стрелок порядка. */
  total: number
  onCompleted: () => void
  onCollapse: () => void
}) {
  const fields = setFields(exercise.tracking)
  const change = (patch: Parameters<typeof changeSet>[2]) => void changeSet(exercise.key, set.id, patch)
  const ref = useRef<HTMLDivElement>(null)
  const timer = useSetTimer()
  // Подход на время во время тренировки — с таймером: «Старт», отсчёт, «Стоп» пишет факт.
  const timed = fields.duration && !draft && !set.completed
  const timing = timer.running && timer.setId === set.id
  const complete = () => {
    if (!set.completed) onCompleted()
    void toggleSet(exercise.key, set.id)
  }
  // Открытый подход — сразу под заголовками (и после перемещения стрелками — стрелки остаются
  // под пальцем), и ещё раз, когда выедет клавиатура.
  useEffect(() => {
    if (ref.current) scrollUnderHeads(ref.current)
  }, [number])
  return (
    <div
      ref={ref}
      onFocus={(e) => {
        if (e.target instanceof HTMLInputElement && ref.current) scrollUnderHeadsAfterKeyboard(ref.current)
      }}
      className="flex flex-col gap-3 rounded-lg border border-accent/40 bg-surface-2/60 p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-expanded
          onClick={onCollapse}
          className="-ml-1 flex min-h-11 flex-1 items-center gap-1.5 rounded-lg px-1 text-left text-sm font-semibold text-fg"
        >
          Подход {number}
          <Icon name="chevron-down" size={18} className="rotate-180 text-muted" />
        </button>
        {total > 1 ? (
          <div className="flex">
            <IconButton
              icon="arrow-up"
              label={`Подход ${number} выше`}
              disabled={number === 1}
              onClick={() => void moveSet(exercise.key, set.id, -1)}
            />
            <IconButton
              icon="arrow-down"
              label={`Подход ${number} ниже`}
              disabled={number === total}
              onClick={() => void moveSet(exercise.key, set.id, 1)}
            />
          </div>
        ) : null}
        {previous && !set.completed ? (
          <IconButton
            icon="copy"
            variant="secondary"
            label={`Копировать подход ${number - 1}`}
            onClick={() =>
              change({
                weight_kg: previous.weight_kg,
                reps: previous.reps,
                duration_s: previous.duration_s,
                distance_m: previous.distance_m,
              })
            }
          />
        ) : null}
      </div>
      {fields.weight ? (
        <NumberStepper
          size="lg"
          label={exercise.tracking === 'bodyweight_reps' ? 'Доп. вес, кг' : 'Вес, кг'}
          value={set.weight_kg}
          onChange={(v) => change({ weight_kg: v })}
          step={WEIGHT_STEP}
          min={0}
          max={1000}
          digits={2}
        />
      ) : null}
      {fields.reps ? (
        <NumberStepper size="lg" label="Повторы" value={set.reps} onChange={(v) => change({ reps: v })} step={1} min={0} max={1000} />
      ) : null}
      {timing ? (
        <SetTimerPanel timer={timer} />
      ) : fields.duration ? (
        <NumberStepper
          size="lg"
          label={timed ? 'Время (цель), с' : 'Время, с'}
          value={set.duration_s}
          onChange={(v) => change({ duration_s: v })}
          step={5}
          min={0}
          max={86400}
        />
      ) : null}
      {fields.distance ? (
        <NumberStepper
          size="lg"
          label="Дистанция, м"
          value={set.distance_m}
          onChange={(v) => change({ distance_m: v })}
          step={10}
          min={0}
          max={1_000_000}
        />
      ) : null}
      {timing ? (
        <div className="flex gap-2">
          <Button size="lg" variant="ghost" onClick={cancelSetTimer}>
            Сброс
          </Button>
          <Button size="lg" block variant="primary" icon="stop" onClick={complete}>
            Стоп и записать
          </Button>
        </div>
      ) : timed ? (
        <div className="flex gap-2">
          <Button size="lg" variant="secondary" icon="check" onClick={complete} aria-label="Выполнен">
            Готово
          </Button>
          <Button size="lg" block variant="primary" icon="play" onClick={() => startSetTimer(exercise, set)}>
            {set.duration_s ? `Старт · цель ${formatClock(set.duration_s)}` : 'Старт секундомера'}
          </Button>
        </div>
      ) : draft ? null : (
        <Button size="lg" block variant={set.completed ? 'secondary' : 'primary'} icon="check" onClick={complete}>
          {set.completed ? 'Снять отметку' : 'Выполнен'}
        </Button>
      )}
      <div className="flex items-center justify-between gap-2">
        <Button size="sm" variant="ghost" className="text-danger" onClick={() => void removeSet(exercise.key, set.id)}>
          Удалить подход
        </Button>
          <button
            type="button"
            aria-pressed={set.is_warmup}
            onClick={() => change({ is_warmup: !set.is_warmup })}
            className={cn(
              'min-h-11 rounded-lg border px-3 text-sm font-medium',
              set.is_warmup ? 'border-accent bg-accent-soft text-accent' : 'border-border text-muted',
            )}
          >
            Разминка
          </button>
      </div>
    </div>
  )
}

/** Идущий таймер в открытом подходе: крупные цифры и подпись цели. */
function SetTimerPanel({ timer }: { timer: SetTimerSnapshot }) {
  const { clock, reached } = setClock(timer)
  return (
    <div role="timer" aria-label="Таймер подхода" className="flex flex-col items-center gap-1 py-2">
      <span className={cn('text-5xl font-semibold tabular-nums', reached ? 'text-accent' : 'text-fg')}>{clock}</span>
      <span className="text-sm text-muted">
        {setTimerLabel(timer, reached)}
        {timer.target !== null ? ` · прошло ${formatClock(timer.elapsed)}` : ''}
      </span>
    </div>
  )
}

function SetLine({
  exercise,
  set,
  number,
  draft,
  onOpen,
}: {
  exercise: ActiveExercise
  set: ActiveSet
  number: number
  draft: boolean
  onOpen: () => void
}) {
  const timer = useSetTimer()
  const timing = timer.running && timer.setId === set.id
  const values = timing ? setClock(timer).clock : formatSet(set, exercise.tracking)
  return (
    <div className="flex min-w-0 items-center gap-2">
      <button
        type="button"
        onClick={onOpen}
        className="flex min-h-12 min-w-0 flex-1 items-center gap-3 rounded-lg px-2 text-left hover:bg-surface-2"
        aria-expanded={false}
        aria-label={`Подход ${number}: ${values}. Изменить`}
      >
        <span className="w-5 shrink-0 text-center text-sm text-muted tabular-nums">{number}</span>
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-base tabular-nums',
            timing ? 'font-semibold text-accent' : set.completed ? 'text-fg' : 'text-muted',
          )}
        >
          {values}
          {set.is_warmup ? <span className="ml-2 text-xs text-muted">разминка</span> : null}
        </span>
      </button>
      {draft ? null : (
        <button
          type="button"
          onClick={() => void toggleSet(exercise.key, set.id)}
          aria-pressed={set.completed}
          aria-label={set.completed ? `Подход ${number} выполнен — снять отметку` : `Отметить подход ${number} выполненным`}
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border',
            set.completed ? 'border-accent bg-accent text-accent-fg' : 'border-border bg-surface text-muted',
          )}
        >
          <Icon name="check" size={22} />
        </button>
      )}
    </div>
  )
}


/** Следующий невыполненный подход после `afterId`, иначе первый невыполненный. */
function nextOpenSet(exercise: ActiveExercise, afterId: string): string | null {
  const i = exercise.sets.findIndex((s) => s.id === afterId)
  const after = exercise.sets.slice(i + 1).find((s) => !s.completed)
  return (after ?? exercise.sets.find((s) => !s.completed && s.id !== afterId))?.id ?? null
}

/**
 * Упражнение. Свёрнуто — название и «выполнено/всего» (зелёным, когда всё). Раскрыто — строки
 * подходов, а заголовок липнет под шапкой тренировки. Подход открывается тапом по строке,
 * сворачивается тапом по «Подход N»; после «Выполнен» открывается следующий. Порядок, замена,
 * техника и «убрать» — полосой в раскрытом упражнении: в заголовке место только для названия.
 */
export function ExerciseCard({
  exercise,
  index,
  total,
  draft,
  open,
  onToggle,
  onMove,
  onReplace,
  onRemove,
}: {
  exercise: ActiveExercise
  index: number
  total: number
  /** Тренировка не начата: в заголовке — сколько подходов, отмечать нечего. */
  draft: boolean
  open: boolean
  onToggle: () => void
  onMove: (delta: -1 | 1) => void
  onReplace: (exercise: Exercise) => void
  onRemove: () => void
}) {
  const [openSet, setOpenSet] = useState<string | null>(null)
  const done = exercise.sets.filter((s) => s.completed).length
  const complete = exercise.sets.length > 0 && done === exercise.sets.length
  const bodyId = `exercise-${exercise.key}`
  const muscle = useExerciseMuscle(exercise.exercise_id)

  return (
    <section aria-label={exercise.name} className="min-w-0 rounded-xl border border-border bg-surface px-3">
      <div
        data-exercise-head
        className={cn(
          'flex items-center gap-1 rounded-xl bg-surface',
          open && 'sticky top-[var(--workout-head-h,0px)] z-10 -mx-3 rounded-b-none border-b border-border px-3',
        )}
      >
        <button
          type="button"
          aria-expanded={open}
          aria-controls={open ? bodyId : undefined}
          onClick={onToggle}
          className="flex min-h-14 min-w-0 flex-1 items-center gap-2 py-2 text-left"
        >
          <span className="flex min-w-0 flex-1 flex-col">
            <h2 className="line-clamp-2 text-base font-semibold leading-snug text-fg">{exercise.name}</h2>
            {muscle ? <span className="truncate text-xs text-muted">{muscle}</span> : null}
          </span>
          <span className={cn('shrink-0 text-sm tabular-nums', complete ? 'font-semibold text-accent' : 'text-muted')}>
            {draft ? `${exercise.sets.length} подх.` : `${done}/${exercise.sets.length}`}
          </span>
          <Icon
            name="chevron-down"
            size={20}
            className={cn('shrink-0 text-muted transition-transform', open && 'rotate-180')}
          />
        </button>
      </div>
      {open ? (
        <div id={bodyId} className="pb-3 pt-1">
          <ExerciseActions
            exerciseId={exercise.exercise_id}
            name={exercise.name}
            index={index}
            total={total}
            onMove={onMove}
            onReplace={onReplace}
            onRemove={onRemove}
            className="-mx-2 mb-1"
          />
          {exercise.last_summary || exercise.reason || exercise.warning ? (
            <div className="mb-2">
              {exercise.last_summary ? (
                <p className="text-sm text-muted tabular-nums">в прошлый раз: {exercise.last_summary}</p>
              ) : null}
              {exercise.reason ? <p className="text-sm text-accent">{exercise.reason}</p> : null}
              {exercise.warning ? (
                <p className="mt-1 flex items-center gap-1 text-sm text-warning">
                  <Icon name="alert" size={14} />
                  {exercise.warning}
                </p>
              ) : null}
            </div>
          ) : null}
          <div className="flex flex-col gap-1.5">
            {exercise.sets.map((set, i) =>
              set.id === openSet ? (
                <SetEditor
                  key={set.id}
                  exercise={exercise}
                  set={set}
                  number={i + 1}
                  previous={exercise.sets[i - 1] ?? null}
                  total={exercise.sets.length}
                  draft={draft}
                  onCompleted={() => setOpenSet(nextOpenSet(exercise, set.id))}
                  onCollapse={() => setOpenSet(null)}
                />
              ) : (
                <SetLine
                  key={set.id}
                  exercise={exercise}
                  set={set}
                  number={i + 1}
                  draft={draft}
                  onOpen={() => setOpenSet(set.id)}
                />
              ),
            )}
          </div>
          <Button
            className="mt-2"
            block
            icon="plus"
            onClick={() =>
              void addSet(exercise.key).then((id) => {
                if (id) setOpenSet(id)
              })
            }
          >
            Подход
          </Button>
        </div>
      ) : null}
    </section>
  )
}
